import {api} from "@/shared/api";
import * as Input from "@/shared/utils/input";

import {DialogMode, RecordDialog, RecordDialogConfig} from "@/shared/recording/recordDialog";
import {isDriveLogDateTimeChangeAllowed} from "@/shared/securityHelper";
import {Combobox} from "@/shared/ui/combobox";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {mergeProperties} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {formatDateTime, getISOZonedDateTime, normalizeTimeInput, Temporal} from "@/shared/utils/datefunctions";
import {Car, DriveLogCategory, ReportDriveLog, ReportDriveLogCategory, User} from "@/shared/timr";
import {Platform, RecordedEntryStatus} from "@/shared/timrTypes";

export interface DriveLogDialogConfig extends RecordDialogConfig {
	cars: Car[];
	driveLogCategories: DriveLogCategory[];
}

export class DriveLogDialog extends RecordDialog<ReportDriveLog> {

	declare private carCombobox: Combobox<Car>;
	declare private categoryCombobox: Combobox<ReportDriveLogCategory>;

	declare public config: DriveLogDialogConfig;

	constructor(config: DriveLogDialogConfig) {
		config = mergeProperties({
			title: {
				Create: "driveLog.header.add",
				Edit: "driveLog.header.edit",
				Show: "driveLog.header",
				Duplicate: "driveLog.header.duplicate"
			},
			users: [],
			cars: []
		}, config);
		super(config);
	}

	protected initDialog() {
		super.initDialog();

		this.initCarSelect();
		this.initCategorySelect();
		this.updateDriveLogInfoPanel();

		$(this.categoryCombobox).on(Combobox.EVENT_CHANGED, () => {
			this.updateBorder();
		});
	}

	public create(lastDriveLog?: Partial<ReportDriveLog>, createAnother = false) {
		super.create(lastDriveLog, createAnother);

		super.initCustomFields();

		if (!SecurityStatus.isAddDriveLogAllowed()) {
			BootstrapModal.info({
				title: "account.expired.drivelog.title",
				message: "account.expired.drivelog",
				escapeText: false
			});
			return;
		}

		if (!createAnother) {
			if (lastDriveLog) {
				if (lastDriveLog.carId) {
					this.carCombobox.setSelectedItemById(lastDriveLog.carId);
				}
				if (lastDriveLog.category) {
					this.categoryCombobox.setSelectedItemById(lastDriveLog.category.id);
				}
			}
		}

		$("#driveLogDialogStatus").val(this.config.defaultStatusNumber);
		$("#driveLogDialogRoute").val("");
		$("#driveLogDialogPurpose").val("");
		$("#driveLogDialogVisited").val("");
		this.toggleTourRecordingField(false);
		this.updateMileage();

		this.carCombobox.setDisabled(false);
		this.categoryCombobox.setDisabled(false);

		this.showDialog();
	}

	protected showRecording(recording: ReportDriveLog, duplicate = false) {
		super.showRecording(recording, duplicate);

		this.updateCustomFields(recording, this.config.customFieldDefinitions);

		$("#driveLogDialogRoute").val(recording.route);
		$("#driveLogDialogPurpose").val(recording.purpose);
		$("#driveLogDialogVisited").val(recording.visited);
		$("#driveLogDialogStartMileage").val(recording.startMileage);
		$("#driveLogDialogEndMileage").val(recording.endMileage);
		if (this.dialogMode === DialogMode.Show && (recording.startPlatform === Platform.TOUR_MOBILE || recording.endPlatform === Platform.TOUR_MOBILE)) {
			$("#driveLogDialogCategory").text(recording.category.name);
			this.toggleTourRecordingField(true);
			this.toggleTourRecordingBusinessFlag(recording.category.business);
		} else {
			this.toggleTourRecordingField(false);
		}
		const car = {id: recording.carId, name: recording.carName, plate: recording.plate};
		this.carCombobox.updateItems([car], car);
		this.carCombobox.setSelectedItemById(recording.carId);

		this.carCombobox.setDisabled(!duplicate);

		/*
		 * If a drive log is opened with a category that has been archived, the combobox should still contain this element
		 * This archived category should be removed afterwards
		 */
		if (this.categoryCombobox.getItems().length !== this.config.driveLogCategories.length) {
			this.categoryCombobox.updateItems(this.config.driveLogCategories);
		}
		this.updateBorder();
		if (recording.category) {
			this.categoryCombobox.setSelectedItem(recording.category, null, true);
		}
	}

	protected _delete() {
		const driveLogId = $(this.config.dialogId).find(".id").val();
		if (!driveLogId) {
			return;
		}
		const route = $(this.config.dialogId).find("#driveLogDialogRoute").val() as string;
		const car = this.carCombobox.getSelectedItem() ? this.carCombobox.getSelectedItem().name : "";
		BootstrapModal.confirm({
			title: "drivelog.delete.title",
			message: messages.withArguments(
				"drivelog.delete.message",
				car,
				route ? " " + messages.withArguments("drivelog.delete.message.route", route) : "",
				formatDateTime(this.parseStartDateTime())
			),
			yesLabel: "confirmation.delete",
			noLabel: "confirmation.cancel",
			yesAction: (modal: BootstrapModal.Modal) => {
				api("recording/deleteDriveLog.json")
					.post({id: driveLogId})
					.done(() => {
						modal.close();
						this.deleteDone();
					});
			},
			noAction: (modal) => {
				modal.close();
				this.deleteCancelled();
			}
		});
	}

	protected save() {
		if (!this.checkBeforeSubmit()) {
			this.dialog.resetLoadingButtons();
			return;
		}
		api("recording/saveDriveLog.json")
			.postForm($("#driveLogDialogForm"))
			.done(() => {
				const carId = parseInt($("#driveLogDialogCarId").val() as string, 10);
				const endOfLastDrive = parseInt($("#driveLogDialogEndMileage").val() as string, 10);
				this.updateEndOfLastDrive(carId, endOfLastDrive);
				if (this.dialogMode === DialogMode.Create && this.dialog.getCheckboxValue()) {
					this.saveRecordDone({
						end: getISOZonedDateTime(this.parseEndDateTime()),
						userId: this.getSelectedUserId()
					});
				} else {
					this.saveRecordDone();
				}
			})
			.fail(() => {
				this.saveFailed();
			});
	}

	/**
	 * Validates the form before submitting. Returns false (and shows feedback) when the entry must not be sent to
	 * the backend, so the caller can abort and reset the loading buttons.
	 */
	private checkBeforeSubmit(): boolean {

		normalizeTimeInput($("#driveLogDialogStartTime"));
		normalizeTimeInput($("#driveLogDialogEndTime"));
		$("#driveLogDialogRoute").focus();
		const startDateTime = this.parseStartDateTime();
		const endDateTime = this.parseEndDateTime();
		if (startDateTime == null) {
			BootstrapModal.error({message: "invalid.start.datetime"});
			return false;
		}
		const driveLogDialogStartTimeZone = $("#driveLogDialogStartTimeZone").val() as string;
		if (!globals.timeZonePattern.test(driveLogDialogStartTimeZone)) {
			BootstrapModal.error({message: "invalid.start.timezone"});
			return false;
		}
		const driveLogDialogStartDateTime = $("#driveLogDialogStartDateTime");
		driveLogDialogStartDateTime.val(getISOZonedDateTime(startDateTime));
		if (endDateTime == null) {
			BootstrapModal.error({message: "invalid.end.datetime"});
			return false;
		}
		const driveLogDialogEndTimeZone = $("#driveLogDialogEndTimeZone").val() as string;
		if (!globals.timeZonePattern.test(driveLogDialogEndTimeZone)) {
			BootstrapModal.error({message: "invalid.end.timezone"});
			return false;
		}
		const driveLogDialogEndDateTime = $("#driveLogDialogEndDateTime");
		driveLogDialogEndDateTime.val(getISOZonedDateTime(endDateTime));
		Input.removeFeedback($("#driveLogDialogEndDate"));
		if (Temporal.ZonedDateTime.compare(startDateTime, endDateTime) > 0) {
			Input.addFeedback($("#driveLogDialogEndDate"), "has-error", messages["saveRecord.endBeforeStart"]);
			return false;
		}
		if (parseInt($("#driveLogDialogStartMileage").val() as string, 10) > parseInt($("#driveLogDialogEndMileage").val() as string, 10)) {
			BootstrapModal.error({message: "dialog.startnotbeforeend"});
			return false;
		}
		if ($("#driveLogDialogUserFilter").is(":visible") && !$("#driveLogDialogUserId").val()) {
			BootstrapModal.error({message: "saveRecord.user.required"});
			return false;
		}
		return true;
	}

	protected onSelectedUserChanged(user: User) {
		super.onSelectedUserChanged(user);

		if (this.dialogMode === DialogMode.Create || this.dialogMode === DialogMode.Duplicate) {
			this.reloadCars();
		}
	}

	private updateEndOfLastDrive(carId: number, endOfLastDrive: number) {
		$.each(this.config.cars, (index, car) => {
			if (car.id === carId) {
				car.endOfLastDrive = endOfLastDrive;
			}
		});
		const selectedCar = this.carCombobox.getSelectedItem();
		if (selectedCar?.id === carId) {
			selectedCar.endOfLastDrive = endOfLastDrive;
		}
	}

	private reloadCars() {
		const userId = $(this.config.dialogId).find(".userId").val();
		if (userId) {
			api("recording/findAssignedUsableCars.json")
				.get({userId: userId})
				.done((data) => {
					this.carCombobox.updateItems(data.cars, null, true);
					/*
					 * there is a problem with loading the following css styles in the combobox library
					 * usually this is set up automatically, but in this case it doesn't, so it is set up here
					 * it seems that this is a time/loading problem, because is also loaded with a large timeout at updateItems() a line before, but this is faster
					 */
					this.dom.find(".carFilter.tt-hint")
						.css("background", "none 0% 0% / auto repeat scroll padding-box border-box rgb(255, 255, 255)");
				});
		}
	}

	private initCarSelect() {
		this.carCombobox = new Combobox({
			items: this.config.cars,
			formatter: (car) => {
				return `${car.plate} - ${car.name}`;
			},
			idElement: $("#driveLogDialogCarId"),
			filterElement: $("#driveLogDialogCarFilter"),
			caretElement: $("#driveLogDialogCarCaret"),
			clearable: false
		});
		$(this.carCombobox).on(Combobox.EVENT_CHANGED, (_e) => {
			this.onSelectedCarChanged();
		});
	}

	private onSelectedCarChanged() {
		if (this.dialogMode === DialogMode.Create) {
			this.updateMileage();
		}
	}

	private initCategorySelect() {
		this.categoryCombobox = new Combobox<ReportDriveLogCategory>({
			items: this.config.driveLogCategories,
			formatter: driveLogCategory => driveLogCategory.name,
			idElement: $("#driveLogDialogCategoryId"),
			filterElement: $("#driveLogDialogCategoryFilter"),
			caretElement: $("#driveLogDialogCategoryCaret"),
			clearable: false
		});
		this.categoryCombobox.selectFirstItem();
	}

	private updateMileage() {
		const endOfLastDrive = this.getEndOfLastDriveForSelectedCar();
		this.dom.find("#driveLogDialogStartMileage").val(endOfLastDrive);
		this.dom.find("#driveLogDialogEndMileage").val(endOfLastDrive);
	}

	private getEndOfLastDriveForSelectedCar(): number {
		const selectedCar = this.carCombobox.getSelectedItem();
		return selectedCar ? selectedCar.endOfLastDrive : null;
	}

	private updateDriveLogInfoPanel() {
		const infoPanel = $("#driveLogDialogInfoPanel");
		const tipField = infoPanel.find(".driveLogTipField");
		infoPanel.toggle(tipField.length > 0);
	}

	protected updateBorder() {
		const business = this.categoryCombobox.getSelectedItem().business;
		const modalContent = this.dom.parents("div.modal-content");

		modalContent.toggleClass("border-BUSINESS", business);
		modalContent.toggleClass("border-PRIVATE", !business);
		modalContent.removeClass("border-NOT_INITIALIZED");
	}

	private toggleTourRecordingField(tourEntry: boolean) {
		$("#driveLogDialogBusinessCheckboxContainer").toggle(!tourEntry);
		$("#driveLogDialogTourCategoryContainer").toggle(tourEntry);
	}

	private toggleTourRecordingBusinessFlag(business: boolean) {
		$("#driveLogDialogTourCategoryContainer i").toggle(business);
	}

	protected isDateTimeChangeAllowed(userId?: number): boolean {
		return isDriveLogDateTimeChangeAllowed(false, userId || this.getSelectedUserId(), this.config.allowedUserIds);
	}

	protected getStatus(record: ReportDriveLog): RecordedEntryStatus {
		return record.status;
	}
}
